import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Target, LockKey, Sparkle, FloppyDisk, ArrowsClockwise, Users, Info, Trash } from "@phosphor-icons/react";

const FACET_LABEL = { geoLocations: "Location", industries: "Industry", seniorities: "Job Seniority", functions: "Job Function", staffCountRanges: "Company Size" };

export default function SocialAudiencePanel({ selected, value, onChange }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [cfg, setCfg] = useState(null);
    const [saved, setSaved] = useState([]);
    const [estimate, setEstimate] = useState(null);
    const [estimating, setEstimating] = useState(false);
    const [toggling, setToggling] = useState(false);

    const loadCfg = () => api.get("/social/audience/config").then(({ data }) => setCfg(data));
    useEffect(() => { loadCfg(); api.get("/social/audiences").then(({ data }) => setSaved(data.audiences || [])).catch(() => {}); }, []);

    const wantsLi = selected.includes("linkedin");
    const wantsFb = selected.includes("facebook");
    const wantsIg = selected.includes("instagram");
    if (!cfg || (!wantsLi && !wantsFb && !wantsIg)) return null;

    const ent = cfg.entitlement || {};

    // ---- premium lock / off states ----
    if (!ent.plan_ok) {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="social-audience-locked">
                <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-600"><LockKey size={20} weight="fill" /></div>
                    <div>
                        <div className="flex items-center gap-2 font-heading text-sm font-bold text-ink">Audience Targeting <span className="rounded-full bg-ink px-2 py-0.5 text-[10px] font-bold text-white">PREMIUM</span></div>
                        <p className="mt-1 text-sm text-stone-600">Reach the right people — target your organic post by industry, seniority, geography and company size on LinkedIn, or restrict by country and age on Facebook. Upgrade to Professional to unlock.</p>
                    </div>
                </div>
            </div>
        );
    }

    const setToggle = async (enabled) => {
        setToggling(true);
        try { await api.post("/social/audience/toggle", { enabled }); await loadCfg(); toast.success(enabled ? "Audience Targeting on" : "Audience Targeting off"); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setToggling(false); }
    };

    if (!ent.enabled) {
        return (
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5" data-testid="social-audience-off">
                <div className="flex items-center gap-3">
                    <Target size={18} className="text-stone-400" />
                    <div className="flex-1"><div className="text-sm font-bold text-ink">Audience Targeting is off</div><div className="text-xs text-stone-500">Turned off for this workspace.</div></div>
                    {isAdmin && <button data-testid="social-audience-enable" disabled={toggling} onClick={() => setToggle(true)} className="rounded-lg bg-ink px-3 py-1.5 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50">Turn on</button>}
                </div>
            </div>
        );
    }

    // ---- active targeting UI ----
    const li = value.linkedin || {};
    const fb = value.facebook || {};
    const setLi = (facet, urns) => onChange({ ...value, linkedin: { ...li, [facet]: urns } });
    const setFb = (patch) => onChange({ ...value, facebook: { ...fb, ...patch } });
    const toggleUrn = (facet, urn) => { const cur = li[facet] || []; setLi(facet, cur.includes(urn) ? cur.filter((x) => x !== urn) : [...cur, urn]); setEstimate(null); };
    const toggleCountry = (code) => { const cur = fb.countries || []; setFb({ countries: cur.includes(code) ? cur.filter((x) => x !== code) : [...cur, code] }); };

    const liHasSpec = Object.values(li).some((a) => Array.isArray(a) && a.length);

    const runEstimate = async () => {
        setEstimating(true); setEstimate(null);
        try { const { data } = await api.post("/social/audience/estimate", { spec: li }); setEstimate(data); }
        catch (e) { setEstimate({ available: false, reason: formatApiError(e?.response?.data?.detail) }); }
        finally { setEstimating(false); }
    };

    const saveAudience = async (platform, spec) => {
        const name = window.prompt("Name this audience");
        if (!name) return;
        try { await api.post("/social/audiences", { name, platform, spec }); const { data } = await api.get("/social/audiences"); setSaved(data.audiences || []); toast.success("Audience saved"); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    const delAudience = async (id) => { try { await api.delete(`/social/audiences/${id}`); setSaved((s) => s.filter((a) => a.id !== id)); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
    const applySaved = (a) => { if (a.platform === "linkedin") { onChange({ ...value, linkedin: a.spec || {} }); setEstimate(null); } else { onChange({ ...value, facebook: a.spec || {} }); } toast.success(`Applied “${a.name}”`); };

    return (
        <div className="rounded-2xl border border-brand/30 bg-brand-soft/30 p-5" data-testid="social-audience-panel">
            <div className="mb-3 flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand text-white"><Target size={16} weight="fill" /></div>
                <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-bold text-ink">Audience Targeting <span className="rounded-full bg-ink px-2 py-0.5 text-[10px] font-bold text-white">PREMIUM</span></div>
                    <div className="text-xs text-stone-500">Shape who sees this organic post.</div>
                </div>
                {isAdmin && <button data-testid="social-audience-disable" disabled={toggling} onClick={() => setToggle(false)} className="text-xs font-semibold text-stone-400 hover:text-stone-600">Turn off</button>}
            </div>

            {/* saved audiences */}
            {saved.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-stone-500">Saved:</span>
                    {saved.map((a) => (
                        <span key={a.id} className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white py-0.5 pl-2.5 pr-1 text-xs font-semibold text-ink">
                            <button data-testid={`social-audience-apply-${a.id}`} onClick={() => applySaved(a)} className="hover:text-brand">{a.name}</button>
                            <button onClick={() => delAudience(a.id)} className="rounded-full p-0.5 text-stone-400 hover:text-red-500"><Trash size={11} /></button>
                        </span>
                    ))}
                </div>
            )}

            {/* LinkedIn */}
            {wantsLi && (
                <div className="mb-4" data-testid="social-audience-linkedin">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#0A66C2]">LinkedIn</div>
                    {!cfg.linkedin.company_page_connected ? (
                        <p className="rounded-lg bg-white/70 p-3 text-xs text-stone-500"><Info size={13} className="mr-1 inline" /> Connect a LinkedIn <b>Company Page</b> in Channels & Setup to target by audience (personal profiles can't be targeted).</p>
                    ) : (
                        <div className="space-y-3">
                            {cfg.linkedin.order.map(([facet]) => (
                                <div key={facet}>
                                    <div className="mb-1 text-[11px] font-semibold text-stone-500">{FACET_LABEL[facet] || facet}</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {(cfg.linkedin.facets[facet] || []).map((o) => {
                                            const on = (li[facet] || []).includes(o.urn);
                                            return <button key={o.urn} type="button" data-testid={`social-li-facet-${facet}-${o.urn}`} onClick={() => toggleUrn(facet, o.urn)}
                                                className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-all ${on ? "border-brand bg-brand text-white" : "border-stone-200 bg-white text-stone2 hover:border-brand/50"}`}>{o.label}</button>;
                                        })}
                                    </div>
                                </div>
                            ))}
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                <button type="button" data-testid="social-audience-estimate" disabled={!liHasSpec || estimating} onClick={runEstimate} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-bold text-ink hover:bg-stone-50 disabled:opacity-50"><Users size={13} /> {estimating ? "Estimating…" : "Estimate reach"}</button>
                                {liHasSpec && <button type="button" onClick={() => saveAudience("linkedin", li)} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-bold text-stone2 hover:bg-stone-50"><FloppyDisk size={13} /> Save audience</button>}
                                {li && Object.keys(li).length > 0 && <button type="button" onClick={() => { onChange({ ...value, linkedin: {} }); setEstimate(null); }} className="text-xs font-semibold text-stone-400 hover:text-stone-600">Clear</button>}
                            </div>
                            {estimate && (
                                <div className={`rounded-lg px-3 py-2 text-xs ${estimate.available ? (estimate.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700") : "bg-stone-100 text-stone-500"}`} data-testid="social-audience-estimate-result">
                                    {estimate.available
                                        ? <><b>~{Number(estimate.count).toLocaleString()}</b> members{!estimate.ok && ` — below LinkedIn's ${estimate.min} minimum; broaden your audience to publish.`}</>
                                        : <>Reach estimate unavailable ({estimate.reason}). LinkedIn still enforces a {estimate.min}-member minimum at publish.</>}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Facebook */}
            {wantsFb && (
                <div className="mb-4" data-testid="social-audience-facebook">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#1877F2]">Facebook <span className="font-normal text-stone-400">— audience restriction</span></div>
                    <div className="mb-1 text-[11px] font-semibold text-stone-500">Countries</div>
                    <div className="mb-3 flex flex-wrap gap-1.5">
                        {cfg.facebook.countries.map((c) => {
                            const on = (fb.countries || []).includes(c.code);
                            return <button key={c.code} type="button" data-testid={`social-fb-country-${c.code}`} onClick={() => toggleCountry(c.code)}
                                className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-all ${on ? "border-brand bg-brand text-white" : "border-stone-200 bg-white text-stone2 hover:border-brand/50"}`}>{c.label}</button>;
                        })}
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <span className="text-[11px] font-semibold text-stone-500">Minimum age</span>
                        <select data-testid="social-fb-age" value={fb.age_min || ""} onChange={(e) => setFb({ age_min: e.target.value ? Number(e.target.value) : undefined })} className="rounded-lg border border-stone-200 px-2 py-1 text-xs focus:border-brand focus:outline-none">
                            <option value="">Any</option>{cfg.facebook.ages.map((a) => <option key={a} value={a}>{a}+</option>)}
                        </select>
                        {(fb.countries?.length || fb.age_min) && <button type="button" onClick={() => saveAudience("facebook", fb)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-bold text-stone2 hover:bg-stone-50"><FloppyDisk size={13} /> Save</button>}
                    </label>
                </div>
            )}

            {/* Instagram */}
            {wantsIg && (
                <p className="rounded-lg bg-white/70 p-3 text-xs text-stone-500" data-testid="social-audience-instagram"><Info size={13} className="mr-1 inline" /> Instagram doesn't support audience targeting on organic posts — it will publish to your full audience.</p>
            )}

            <p className="mt-1 flex items-center gap-1.5 text-[11px] text-stone-400"><Sparkle size={12} weight="fill" /> Organic targeting shapes who's most likely to see your post — it isn't a paid ad.</p>
        </div>
    );
}
